import React from 'react';
import classNames from 'classnames';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import Button from './Button';

export interface PaginationProps {
  current: number;
  total: number;
  pageSize: number;
  onChange: (page: number, pageSize?: number) => void;
  onShowSizeChange?: (current: number, size: number) => void;
  showSizeChanger?: boolean;
  showQuickJumper?: boolean;
  showTotal?: boolean | ((total: number, range: [number, number]) => React.ReactNode);
  pageSizeOptions?: string[];
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  disabled?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  current,
  total,
  pageSize,
  onChange,
  onShowSizeChange,
  showSizeChanger = true,
  showQuickJumper = false,
  showTotal = true,
  pageSizeOptions = ['10', '20', '50', '100'],
  size = 'md',
  className,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const totalPages = Math.ceil(total / pageSize);
  const startItem = (current - 1) * pageSize + 1;
  const endItem = Math.min(current * pageSize, total);

  // Custom pagination button to ensure proper active state styling
  const PaginationButton: React.FC<{
    page: number;
    isActive: boolean;
    onClick: () => void;
    disabled?: boolean;
  }> = ({ page, isActive, onClick, disabled }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={classNames(
        'inline-flex items-center justify-center font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2',
        buttonSizeClasses[size],
        isActive
          ? 'bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500'
          : 'border border-gray-300 hover:bg-gray-50 text-gray-700 focus:ring-gray-500',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
      data-page={page}
      data-is-active={isActive}
    >
      {page}
    </button>
  );

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== current && !disabled) {
      onChange(page, pageSize);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (onShowSizeChange && !disabled) {
      onShowSizeChange(current, newPageSize);
    }
  };

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, current - delta); i <= Math.min(totalPages - 1, current + delta); i++) {
      range.push(i);
    }

    if (current - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (current + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const buttonSizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
  };

  if (total === 0) {
    return null;
  }

  return (
    <div className={classNames('flex items-center justify-between', sizeClasses[size], className)}>
      {/* Total info */}
      <div className="flex items-center space-x-4">
        {showTotal && (
          <span className="text-gray-700">
            {typeof showTotal === 'function' 
              ? showTotal(total, [startItem, endItem])
              : `${t('common.total')} ${total} ${t('common.items')}`
            }
          </span>
        )}
        
        {showTotal && showSizeChanger && (
          <span className="text-gray-400">|</span>
        )}
        
        {showSizeChanger && (
          <div className="flex items-center space-x-2">
            <span className="text-gray-700">{t('common.perPage')}:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              disabled={disabled}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Pagination controls */}
      <div className="flex items-center space-x-1">
        {/* Previous button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(current - 1)}
          disabled={current <= 1 || disabled}
          icon={<HiChevronLeft className="h-4 w-4" />}
          className={buttonSizeClasses[size]}
          aria-label="previous"
        />

        {/* Page numbers */}
        {getVisiblePages().map((page, index) => (
          <React.Fragment key={index}>
            {page === '...' ? (
              <span className="px-2 py-1 text-gray-500">...</span>
            ) : (
              <PaginationButton
                page={page as number}
                isActive={Number(page) === Number(current)}
                onClick={() => handlePageChange(page as number)}
                disabled={disabled}
              />
            )}
          </React.Fragment>
        ))}

        {/* Next button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(current + 1)}
          disabled={current >= totalPages || disabled}
          icon={<HiChevronRight className="h-4 w-4" />}
          className={buttonSizeClasses[size]}
          aria-label="next"
        />
      </div>

      {/* Quick jumper */}
      {showQuickJumper && (
        <div className="flex items-center space-x-2">
          <span className="text-gray-700">{t('common.page')}:</span>
          <input
            type="number"
            min={1}
            max={totalPages}
            defaultValue={current}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const value = parseInt((e.target as HTMLInputElement).value);
                handlePageChange(value);
              }
            }}
            disabled={disabled}
            className="w-16 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      )}
    </div>
  );
};

export default Pagination;
